/**
 * DubRunProgress.tsx — PLAT-11714 (follows PLAT-11688)
 *
 * A running dub, reported where there is room to report it properly: the
 * spinner, the language, an estimated percent and a determinate bar, with the
 * Cancel the chip has.
 *
 * `DubGenerateModal` closes the instant it hands the request to
 * `DubbingStore.startRun`, on the premise that the page behind it already
 * carries the run — which is true of the transcript toolbar (`DubRunChip`) and
 * was NOT true of the Translation Review, whose player header is a surface of
 * its own. Generating from inside the review therefore looked like nothing had
 * happened. This is that surface's report.
 *
 * Presentation only: the caller resolves the run (`useActiveDubRun`) and so
 * re-renders once when it starts and once when it ends, while the half-second
 * progress tick stays in here.
 */
import ProgressBar from '@src/components/common/ProgressBar';
import {
  InProgressChip,
  InProgressSpinner,
  InProgressText,
} from '@src/components/transcript/inProgressChipStyles';
import { useDubbingStore } from '@src/stores-v2/StoreContext';
import CloseIcon from '@src/svgs/CloseIcon';
import styled from 'styled-components';

import { useDubGenerateProgress } from './useDubGenerateProgress';

import type { DubRun } from '@src/stores-v2/DubbingStore';

export function DubRunProgress({ run }: { run: DubRun }) {
  const dubbingStore = useDubbingStore();
  const progress = useDubGenerateProgress(true, run);
  const percent = Math.round(progress);

  return (
    <InProgressChip
      role="status"
      aria-live="polite"
      aria-label={`Dubbing ${run.languageCaption}, ${percent}%`}
      data-testid="review-dub-run-progress"
    >
      <InProgressSpinner />
      <InProgressText>Dubbing…</InProgressText>
      <ProgressBar value={percent} max={100} height={4} width={96} />
      <RunPercent>{percent}%</RunPercent>
      <CancelButton
        type="button"
        aria-label="Cancel dub"
        onClick={() => dubbingStore.cancelRun()}
      >
        <CloseIcon width={10} height={10} />
      </CancelButton>
    </InProgressChip>
  );
}

/** Never wrapped or truncated: the number is the point of the row. */
const RunPercent = styled(InProgressText)`
  flex: none;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const CancelButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: var(--radius-button);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;

  &:hover {
    background-color: var(--surface-elevated);
    color: var(--foreground);
  }
`;
